// Diff simples POR LINHA (LCS) para o comparador de versões do
// editor de templates (§4.5). Sem lib externa — o caso de uso é
// comparar sources de template (dezenas/centenas de linhas), então
// o O(n*m) do LCS é mais que suficiente.

export type DiffLineType = "equal" | "added" | "removed"

export type DiffLine = {
  type: DiffLineType
  content: string
  // Números 1-based nas versões A (esquerda) e B (direita);
  // null quando a linha não existe naquele lado.
  aLine: number | null
  bLine: number | null
}

// Compara dois textos linha a linha. `a` = versão base (esquerda),
// `b` = versão comparada (direita). added = só em b, removed = só
// em a.
export const computeLineDiff = (
  a: string,
  b: string
): DiffLine[] => {
  const aLines = a.split("\n")
  const bLines = b.split("\n")
  const n = aLines.length
  const m = bLines.length

  // Tabela LCS (n+1 x m+1).
  const lcs: number[][] = Array.from(
    { length: n + 1 },
    () => new Array<number>(m + 1).fill(0)
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        aLines[i] === bLines[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (aLines[i] === bLines[j]) {
      result.push({
        type: "equal",
        content: aLines[i],
        aLine: i + 1,
        bLine: j + 1,
      })
      i++
      j++
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      result.push({
        type: "removed",
        content: aLines[i],
        aLine: i + 1,
        bLine: null,
      })
      i++
    } else {
      result.push({
        type: "added",
        content: bLines[j],
        aLine: null,
        bLine: j + 1,
      })
      j++
    }
  }
  while (i < n) {
    result.push({
      type: "removed",
      content: aLines[i],
      aLine: i + 1,
      bLine: null,
    })
    i++
  }
  while (j < m) {
    result.push({
      type: "added",
      content: bLines[j],
      aLine: null,
      bLine: j + 1,
    })
    j++
  }
  return result
}

// Resumo do diff (badge "+N −M" no dialog).
export const summarizeDiff = (lines: DiffLine[]) => ({
  added: lines.filter((line) => line.type === "added")
    .length,
  removed: lines.filter((line) => line.type === "removed")
    .length,
})
